import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const agents = [
  {
    num: '01', id: 'website', accent: '#289199', icon: 'FiGlobe', subtitle: 'WEBSITE BUILDER & AUTO-SCHEMA', title: 'SEO Website Agent',
    hook: 'Google is confused about your business. We fix that instantly — without you lifting a finger.',
    bullets: ['Auto-syncs your site the moment your Google hours or photos change', 'Injects schema markup — the invisible math language search engines trust', 'Aligns your digital frequency so algorithms recognize your true authority', 'No coding. No agency fees. Pure digital alignment.'],
    badge: { title: 'GEO Agent', desc: 'Creates blog posts with local SEO keywords to boost your search rankings', icon: 'FiMapPin' },
  },
  {
    num: '02', id: 'social', accent: '#eb5e55', icon: 'FiShare2', subtitle: 'REVIEW SHARING — AUTOPILOT', title: 'Social Agent',
    hook: "Your 5-star reviews are gold. You're letting them go to waste.",
    bullets: ['Grabs every 4–5 star review the moment it lands', 'Designs a branded graphic and writes the caption automatically', 'Posts to Facebook, Instagram & TikTok without you touching a thing', 'Amplifies happy client energy to magnetize your ideal customers'],
    badge: { title: 'Social Agent', desc: 'Creates and publishes engaging social media posts', icon: 'FiImage', socials: true },
  },
  {
    num: '03', id: 'seo', accent: '#f59e0b', icon: 'FiMessageSquare', subtitle: 'AI REVIEW REPLIES', title: 'SEO Reply Agent',
    hook: 'Google rewards businesses that reply fast. You forget. Our AI never does.',
    bullets: ['Replies to every review within seconds of it landing', 'Writes polite, on-brand responses in your voice', 'Embeds local SEO keywords to boost your search rankings', 'Closes the feedback loop — teaching AI exactly who you are'],
    badge: { title: 'SEO Agent', desc: 'Replies to Google reviews with local SEO keywords to boost your search rankings', icon: 'FiCpu' },
  },
];

function AgentCard({ agent }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="group relative flex flex-col p-4 sm:p-6 rounded-2xl bg-[#181818] border border-white/5 overflow-hidden font-lexend">
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl" style={{ boxShadow: `inset 0 0 0 1px ${agent.accent}, 0 0 40px ${agent.accent}15` }} />
      <div className="relative z-10 flex flex-col h-full">
        <div className="flex items-start justify-between gap-4 mb-4 relative">
          <div className="flex flex-col items-start gap-2">
            <div className="h-9 w-9 sm:h-11 sm:w-11 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: agent.accent }}>
              <SafeIcon name={agent.icon} className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
            </div>
            <span className="text-[9px] font-black uppercase tracking-[0.15em] px-2 py-1 rounded-full border" style={{ backgroundColor: `${agent.accent}15`, color: agent.accent, borderColor: `${agent.accent}30` }}>
              {agent.subtitle}
            </span>
          </div>
          <span className="text-4xl font-michroma text-white/5 right-0 top-0 absolute pointer-events-none">{agent.num}</span>
        </div>
        
        <h3 className="text-base sm:text-lg font-michroma text-white mb-2 leading-tight">{agent.title}</h3>
        <p className="text-xs font-medium text-white/80 leading-snug mb-3 pl-3 border-l-2" style={{ borderColor: agent.accent }}>{agent.hook}</p>
        
        <ul className="space-y-2 mb-4 flex-grow">
          {agent.bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="flex-shrink-0 h-4 w-4 rounded-full flex items-center justify-center mt-0.5" style={{ backgroundColor: `${agent.accent}20` }}>
                <SafeIcon name="FiCheck" className="h-2.5 w-2.5" style={{ color: agent.accent }} />
              </span>
              <span className="text-xs text-white/55 leading-relaxed">{b}</span>
            </li>
          ))}
        </ul>

        <div className="mt-auto flex flex-col gap-3">
          <button onClick={() => setExpanded(!expanded)} className="self-start flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors duration-200" style={{ color: agent.accent }}>
            {expanded ? 'Hide Details' : 'Read the Full Story'}
            <SafeIcon name={expanded ? 'FiChevronUp' : 'FiChevronDown'} className="h-3 w-3" />
          </button>

          <AnimatePresence>
            {expanded && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                <div className="pt-2 pb-1 border-t border-white/10 space-y-2">
                  {[
                    { label: 'The Problem', text: 'Google hides businesses when their site and profile don\'t match. You lose calls, competitors get your money.' },
                    { label: 'The Fix', text: 'A site that updates itself the second you change your Google hours. Schema markup woven directly into the web.' },
                    { label: 'The Result', text: 'Search algorithms have no choice but to recognize your true authority.' },
                  ].map((block, i) => (
                    <div key={i}>
                      <span className="text-[9px] font-black uppercase tracking-widest mb-1 block" style={{ color: agent.accent }}>{block.label}</span>
                      <p className="text-xs text-white/50 leading-relaxed">{block.text}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="w-full rounded-xl p-3 flex items-start gap-2.5 border" style={{ backgroundColor: `${agent.accent}10`, borderColor: `${agent.accent}25` }}>
            <div className="h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${agent.accent}30` }}>
              <SafeIcon name={agent.badge.icon} className="h-3.5 w-3.5 text-white" />
            </div>
            <div>
              <span className="text-xs font-bold text-white tracking-wide mb-0.5 flex items-center gap-1.5">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: agent.accent }} />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ backgroundColor: agent.accent }} />
                </span>
                {agent.badge.title}
              </span>
              <span className="text-[10px] text-white/50 leading-tight">{agent.badge.desc}</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function AgentBaseplates() {
  return (
    <section className="relative w-full min-h-full bg-[#202020] flex flex-col justify-center py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/5 to-transparent" />
      
      <div className="relative z-10 max-w-7xl mx-auto w-full">
        <motion.div initial={{ opacity: 0, y: -20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="text-center mb-7 sm:mb-10">
          <span className="inline-block text-brand-teal font-lexend font-bold text-xs uppercase tracking-[0.2em] mb-3">The Core Engine</span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-michroma text-white tracking-tight leading-tight">
            Enterprise-Level AI. <span className="text-brand-teal drop-shadow-[0_0_15px_rgba(40,145,153,0.3)]">Built for Local Grit.</span>
          </h2>
          <p className="mt-3 text-white/50 font-lexend max-w-xl mx-auto leading-relaxed text-xs sm:text-sm px-4">
            Three AI agents working in the background 24/7 — each one closing a gap that's quietly costing you money.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
          {agents.map((agent) => <AgentCard key={agent.id} agent={agent} />)}
        </div>
      </div>
    </section>
  );
}